/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import CreateNewFolderOutlinedIcon from '@material-ui/icons/CreateNewFolderOutlined';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import SaveIcon from '@material-ui/icons/Save';
import DeleteIcon from '@material-ui/icons/Delete';
import { Colors } from '@silvergatedelivery/constants';
import MealDateSelector from 'components/Date/MealDateSelector';
import { asyncListAll } from 'utilities/graph';
import AutoSelect from './AutoSelect';
import { sortBy } from 'utilities/sorting';
import OrderItem from './OrderItem';
import { getSampleOrder } from './helpers';
import {
  getRestaurantHolidayByRestaurantByYear,
  getRestaurantMealsByRestaurantByIsActive,
} from './queries';
import moment from 'moment';
import { TIME_ZONE, ORDER_CATEGORY, ORDER_CATEGORY_LABEL } from '@silvergatedelivery/constants';
import DataJoinEditorInput from 'components/DataJoinEditor/DataJoinEditorInput';
import { updateFacilitySettings, createFacilitySettings, updateClient } from 'graphql/mutations';
import { request } from 'utilities/graph';
import cache from 'utilities/cache';
import { getClient } from 'graphql/queries';
import { toastr } from 'react-redux-toastr';

const useStyles = makeStyles((theme) => ({
  actionContainer: {
    width: 40,
  },
  itemSummaryContainer: {
    display: 'inline-block',
    width: 32,
    paddingTop: 12,
    fontWeight: 'bold',
  },
  itemContainer: {
    display: 'inline-block',
    flex: 1,
  },
  floatingCloseButton: {
    position: 'absolute',
    right: 10,
    top: 10,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  button: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  icon: {
    fontSize: 24,
  },
  label: {
    fontSize: '0.8rem',
    marginTop: theme.spacing(0.5),
  },
  settingName: {
    marginLeft: 36,
    width: '300px',
  },
}));

export default function OrderBatchForm({
  index,
  targetClientId,
  onRemove,
  onUpdate,
  showMap,
  unlockDates,
  deliveryGroupOptions = [],
  elderDeliveryGroups = [],
  elderOptions = [],
  restaurantOptions = [],
  deliveryStaffOptions = {},
  orderConfigurations = [],
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [deliveryGroupId, setDeliveryGroupId] = useState();
  const [defaultRestaurantId, setDefaultRestaurantId] = useState();
  const [restaurantMealOptions, setRestaurantMealOptions] = useState({});
  const [restaurantHolidays, setRestaurantHolidays] = useState({});
  const [defaultMealItemNames, setDefaultMealItemNames] = useState([]);
  const [defaultCategory, setDefaultCategory] = useState(ORDER_CATEGORY_LABEL[0]);
  const [defaultDeliveryStaffId, setDefaultDeliveryStaffId] = useState();
  const [defaultLunchs, setDefaultLunchs] = useState([]);
  const [defaultDinners, setDefaultDinners] = useState([]);
  const [openingHours, setOpeningHours] = useState();
  const [settingName, setSettingName] = useState('');
  const [originalSettingName, setOriginalSettingName] = useState('');
  const [settingAnchorEl, setSettingAnchorEl] = useState();
  const [batchOrderSettingOptions, setBatchOrderSettingOptions] = useState([]);
  const [client, setClient] = useState();

  const getClientFacilitySettings = async () => {
    if (targetClientId && targetClientId !== 'N/A') {
      const { data: { getClient: data } } = await request(getClient, { id: targetClientId });
      if (data?.facilitySettings?.batchOrderSettings) {
        setBatchOrderSettingOptions(data.facilitySettings.batchOrderSettings.map(({ name, batchOrderSettingsItems }) => {
          return {
            name,
            batchOrderSettingsItems: JSON.parse(batchOrderSettingsItems),
          };
        }));
      }
      setClient(data);
    }
  };

  useEffect(() => {
    (async () => {
      await getClientFacilitySettings(true);
    })();
  }, []);

  const [orders, setOrders] = useState([]);

  const handleUpdate = (orders) => {
    if (onUpdate) {
      onUpdate({
        deliveryGroupId,
        defaultRestaurantId,
        defaultCategory,
        defaultDeliveryStaffId,
        defaultLunchs,
        defaultDinners,
        orders,
      });
    }
  };

  const handleNewSetting = () => {
    let newSetting = true;
    if (orders.length !== 0) {
      if (!window.confirm('確定捨棄目前訂單設定？')) {
        newSetting = false;
      }
    }
    if (newSetting) {
      setSettingName('');
      setOriginalSettingName('');

      // reset
      setDeliveryGroupId();
      setDefaultRestaurantId();
      setDefaultCategory(ORDER_CATEGORY_LABEL[0]);
      setDefaultDeliveryStaffId();
      setDefaultLunchs([]);
      setDefaultDinners([]);
      setOrders([]);

      handleUpdate([]);
    }
  };

  const handleOpenSetting = (event) => {
    setSettingAnchorEl(event.currentTarget);
  };

  const handleSave = async () => {
    if (!client) return;
    const newBatchOrderSettings = {
      name: settingName,
      batchOrderSettingsItems: JSON.stringify({
        deliveryGroupId,
        defaultRestaurantId,
        defaultCategory,
        defaultDeliveryStaffId,
        orders: orders.map((order) => ({ ...order, lunchs: [], dinners: [] })),
      }),
    };

    const { facilitySettings = {} } = client;
    if (facilitySettings.id) {
      if (facilitySettings.batchOrderSettings) {
        const settingIndex = facilitySettings.batchOrderSettings.findIndex(({ name }) => name === settingName);
        if (settingIndex === -1) {
          facilitySettings.batchOrderSettings.push(newBatchOrderSettings);
        } else {
          if (!window.confirm(`確定覆蓋${settingName}原有設定？`)) {
            return;
          }
          facilitySettings.batchOrderSettings[settingIndex] = newBatchOrderSettings;
        }
      } else {
        facilitySettings.batchOrderSettings = [newBatchOrderSettings];
      }
      await request(updateFacilitySettings, { input: facilitySettings });
    } else {
      const now = moment().toISOString();
      const userName = cache.get('app:userName');
      const newFacilitySettings = {
        batchOrderSettings: [newBatchOrderSettings],
        createdAt: now,
        createdBy: userName,
        updatedAt: now,
        updatedBy: userName,
      };
      const { data: { createFacilitySettings: { id: facilitySettingsId } } } =
        await request(createFacilitySettings, { input: newFacilitySettings });
      await request(updateClient, { input: {
        id: client.id,
        type: client.type,
        isActive: client.isActive,
        county: client.county,
        name: client.name,
        address: client.address,
        email: client.email,
        usersLimit: client.usersLimit,
        eldersLimit: client.eldersLimit,
        createdAt: client.createdAt,
        createdBy: client.createdBy,
        updatedAt: now,
        updatedBy: userName,
        facilitySettingsId: facilitySettingsId,
      } });
    }
    await getClientFacilitySettings();
    toastr.info(`設定${settingName}已儲存`);
    setOriginalSettingName(settingName);
  };

  const handleDelete = async () => {
    const { facilitySettings = {} } = client;
    if (facilitySettings.id) {
      if (facilitySettings.batchOrderSettings) {
        const settingIndex = facilitySettings.batchOrderSettings.findIndex(({ name }) => name === settingName);
        if (settingIndex !== -1) {
          if (window.confirm(`確定刪除設定${settingName}？`)) {
            facilitySettings.batchOrderSettings.splice(settingIndex, 1);
            await request(updateFacilitySettings, { input: facilitySettings });
            await getClientFacilitySettings();
            toastr.info(`設定${settingName}已刪除`);
            setSettingName('');
            setOriginalSettingName('');
          }
        }
      }
    }
  };

  const onSettingChange = (setting) => {
    let loadSetting = true;
    if (orders.length !== 0) {
      if (!window.confirm('確定捨棄目前訂單設定？')) {
        loadSetting = false;
      }
    }
    if (loadSetting) {
      const { name, batchOrderSettingsItems } = setting;
      setSettingName(name);
      setOriginalSettingName(name);
      const {
        deliveryGroupId, defaultRestaurantId, defaultCategory, defaultDeliveryStaffId, orders,
      } = batchOrderSettingsItems;
      setDeliveryGroupId(deliveryGroupId);
      setDefaultRestaurantId(defaultRestaurantId);
      setDefaultCategory(defaultCategory);
      setDefaultDeliveryStaffId(defaultDeliveryStaffId);
      setDefaultLunchs([]);
      setDefaultDinners([]);
      // 先開空白訂單，避免預設的xx設定覆蓋掉載入的order設定
      setOrders(orders.map(() => getSampleOrder()));
      setTimeout(() => {
        setOrders(orders);
        handleUpdate(orders);
      }, 1000);
    }
  };

  return (
    <Grid item xs={12} container style={{ marginTop: 16, borderBottom: `1px solid ${Colors.light2}`, position: 'relative' }}>
      <IconButton
        size={'medium'}
        className={classes.floatingCloseButton}
        onClick={() => {
          if (onRemove) {
            onRemove();
          }
        }}
      >
        <HighlightOffIcon />
      </IconButton>
      <div className={classes.itemContainer}>
        <Grid container>
          <Grid item xs={showMap ? 4 : 6}>
            {targetClientId && targetClientId !== 'N/A' &&
            <Toolbar className={classes.toolbar}>
              <div className={classes.buttonGroup}>
                <div className={classes.button}>
                  <IconButton onClick={handleNewSetting}>
                    <CreateNewFolderOutlinedIcon className={classes.icon} />
                  </IconButton>
                  <Typography className={classes.label}>新設定</Typography>
                </div>
                <div className={classes.button}>
                  <IconButton disabled={batchOrderSettingOptions?.length === 0} onClick={handleOpenSetting}>
                    <FolderOpenIcon className={classes.icon} />
                  </IconButton>
                  <Typography className={classes.label}>開啟設定</Typography>
                </div>
                <Menu
                  id="setting-menu"
                  anchorEl={settingAnchorEl}
                  keepMounted
                  open={Boolean(settingAnchorEl)}
                  onClose={() => setSettingAnchorEl(null)}
                  getContentAnchorEl={null}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                >
                  {batchOrderSettingOptions.sort(sortBy('name')).map((item) => {
                    return (
                      <MenuItem
                        key={item.name}
                        value={item.name}
                        onClick={() => {
                          onSettingChange(item);
                          setSettingAnchorEl(null);
                        }}
                        disabled={item.name === originalSettingName}
                      >
                        {item.name}
                      </MenuItem>
                    );
                  })}
                </Menu>
                <div className={classes.button}>
                  <IconButton
                    disabled={settingName === '' || orders.length === 0}
                    onClick={handleSave}
                  >
                    <SaveIcon className={classes.icon} />
                  </IconButton>
                  <Typography className={classes.label}>儲存設定</Typography>
                </div>
                <div className={classes.button}>
                  <IconButton onClick={handleDelete} disabled={!batchOrderSettingOptions.some(({ name }) => name === settingName)}>
                    <DeleteIcon className={classes.icon} />
                  </IconButton>
                  <Typography className={classes.label}>刪除設定</Typography>
                </div>
              </div>
              <TextField
                className={classes.settingName}
                label='設定名稱'
                type="text"
                value={settingName}
                onChange={(e) => setSettingName(e.target.value)}
                placeholder='請輸入送餐設定的名稱'
              />
            </Toolbar>}
            <FormControl fullWidth style={{ marginTop: 10, marginBottom: 10 }}>
              <AutoSelect
                id={`deliveryGroup-${index}`}
                options={deliveryGroupOptions.map(({ name }) => name)}
                values={deliveryGroupOptions.map(({ id }) => id)}
                required={false}
                value={deliveryGroupId}
                label={t('送餐群組')}
                onChange={(newItem) => {
                  setOrders([]);

                  setDeliveryGroupId(newItem);

                  const orders = elderDeliveryGroups
                    .filter(({ deliveryGroupId }) => deliveryGroupId === newItem)
                    .sort((sortBy('sortOrder')))
                    .map(({ elderId, sortOrder }) => {
                      const elder = elderOptions.find(({ id }) => id === elderId);
                      if (elder) {
                        const { id, noteForDelivery, noteForMeal, note } = elder;
                        return getSampleOrder({
                          elderId: id,
                          noteForDelivery,
                          noteForMeal,
                          note,
                          deliveryGroupId: newItem,
                          deliveryGroupSortOrder: sortOrder,
                        });
                      } else {
                        // 長輩不存在 或 已停用
                        return null;
                      }
                    })
                    .filter((x) => x);

                  setTimeout(()=>{
                    setOrders([...orders]);
                    handleUpdate(orders);
                  }, 200);
                }}
                checkError={true}
              />
            </FormControl>

            <FormControl fullWidth style={{ marginBottom: 10 }}>
              <AutoSelect
                id={`restaurant-${index}`}
                options={restaurantOptions.map(({ address, name }) => (`${address.county} - ${name}`))}
                values={restaurantOptions.map(({ id }) => id)}
                required={true}
                value={defaultRestaurantId}
                label={`預設${t('餐廳')}`}
                onChange={async (newItem)=>{
                  setDefaultRestaurantId(newItem);
                  const restaurantSelected = restaurantOptions.find((restaurantOption) => restaurantOption.id === newItem);
                  setOpeningHours(restaurantSelected ? restaurantSelected.openingHours : undefined);
                  if (newItem && (!restaurantMealOptions[newItem] || !restaurantHolidays[newItem])) {
                    const start = moment().tz(TIME_ZONE).year();
                    const end = start + 1;
                    const [mealOptions, holidays] = await Promise.all([
                      await asyncListAll(getRestaurantMealsByRestaurantByIsActive, { restaurantId: newItem }),
                      await asyncListAll(getRestaurantHolidayByRestaurantByYear, {
                        restaurantId: newItem,
                        year: {
                          between: [start, end],
                        },
                      }),
                    ]);
                    restaurantMealOptions[newItem] = mealOptions;
                    setRestaurantMealOptions({ ...restaurantMealOptions });
                    restaurantHolidays[newItem] = holidays;
                    setRestaurantHolidays({ ...restaurantHolidays });
                  }
                }}
                checkError={true}
              />
            </FormControl>

            <FormControl fullWidth style={{ marginBottom: 10 }}>
              <DataJoinEditorInput
                title={`預設${t('餐點')}名稱`}
                options={restaurantMealOptions[defaultRestaurantId] ?
                  restaurantMealOptions[defaultRestaurantId].map((meal) => ({
                    label: meal.name,
                    description: `進價：$${meal.cost || 0}, 單價：${meal.price || 0}`,
                  })) : []}
                defaultValues={[]}
                onChange={(newMealItemNames) => setDefaultMealItemNames(newMealItemNames.map(({ label }) => label))}
                onUpdateOptions={() => {}}
                showHelperText={false}
                disabled={!defaultRestaurantId}
              />
            </FormControl>

            <FormControl fullWidth style={{ marginBottom: 10 }}>
              <AutoSelect
                id={`category-${index}`}
                options={ORDER_CATEGORY_LABEL}
                values={ORDER_CATEGORY_LABEL}
                descriptions={ORDER_CATEGORY.map(({ description }) => description)}
                required={false}
                freeSolo
                value={defaultCategory}
                label="預設訂單類別"
                onChange={(value)=>{
                  setDefaultCategory(value);
                }}
                checkError={true}
              />
            </FormControl>

            <FormControl fullWidth style={{ marginBottom: 10 }}>
              <AutoSelect
                id={`deliveryStaff-${index}`}
                options={deliveryStaffOptions.enumNames}
                values={deliveryStaffOptions.enum}
                required={false}
                value={defaultDeliveryStaffId}
                label={`預設${t('送餐大使')}`}
                onChange={(newItem)=>{
                  setDefaultDeliveryStaffId(newItem);
                }}
                checkError={true}
              />
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <MealDateSelector
              mealSlot="lunch"
              unlockDates={unlockDates}
              openingHours={openingHours}
              defaultSelectedDays={defaultLunchs}
              forceUpdateDefaultSelectedDays={true}
              holidays={restaurantHolidays[defaultRestaurantId]}
              onChange={(items)=>{
                setDefaultLunchs(items);
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <MealDateSelector
              mealSlot="dinner"
              unlockDates={unlockDates}
              openingHours={openingHours}
              defaultSelectedDays={defaultDinners}
              forceUpdateDefaultSelectedDays={true}
              holidays={restaurantHolidays[defaultRestaurantId]}
              onChange={(items)=>{
                setDefaultDinners(items);
              }}
            />
          </Grid>
        </Grid>

        {orders.map((order, orderIndex) => (
          <React.Fragment key={orderIndex}>
            <OrderItem
              index={orderIndex}
              order={order}
              onRemove={() => {
                orders.splice(orderIndex, 1);
                setOrders([...orders]);
                handleUpdate(orders);
              }}
              onUpdate={(updatedOrder) => {
                order = updatedOrder;
                handleUpdate(orders);
              }}
              elderOptions={elderOptions}
              restaurantOptions={restaurantOptions}
              restaurantMealOptions={restaurantMealOptions}
              deliveryStaffOptions={deliveryStaffOptions}
              orderConfigurations={orderConfigurations}
              defaultLunchs={defaultLunchs}
              defaultDinners={defaultDinners}
              defaultRestaurantId={defaultRestaurantId}
              defaultDeliveryStaffId={defaultDeliveryStaffId}
              defaultMealItemNames={defaultMealItemNames}
              defaultCategory={defaultCategory}
              defaultOpeningHours={openingHours}
              defaultRestaurantHolidays={restaurantHolidays}
              showMap={showMap}
              unlockDates={unlockDates}
            />
          </React.Fragment>
        ))}

        <div style={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            style={{ height: 24, margin: 24 }}
            onClick={() => {
              orders.push(getSampleOrder());
              setOrders([...orders]);
              handleUpdate(orders);
            }}
          >
            {`新增${t('送餐對象')}`}
          </Button>
        </div>
      </div>
    </Grid>);
}
